import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { API_BASE_URL, API_ENDPOINTS, DEFAULT_HEADERS, getAuthHeaders } from '../config/api.ts';

interface Staff {
  id: string;
  _id: string;
  name: string;
  email: string;
  phone: string;
  role: string;
  branch?: {
    _id: string;
    name: string;
    code: string;
  };
  position: string;
  isSuperAdmin: boolean;
  isActive: boolean;
  permissions: string[];
  lastLogin: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: Staff | null;
  token: string | null;
  isInitialized: boolean;
  showTokenExpiredModal: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; message?: string; remainingAttempts?: number; lockTime?: number }>;
  logout: () => void;
  refreshProfile: () => Promise<void>;
  refreshToken: (password: string) => Promise<{ success: boolean; message?: string }>;
  closeTokenExpiredModal: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<Staff | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [showTokenExpiredModal, setShowTokenExpiredModal] = useState(false);

  useEffect(() => {
    // Sayfa yüklendiğinde localStorage'dan authentication durumunu kontrol et
    const savedToken = localStorage.getItem('adminToken');
    const savedUser = localStorage.getItem('adminUser');
    
    if (savedToken && savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        
        // Token'ın geçerliliğini kontrol et
        const tokenPayload = JSON.parse(atob(savedToken.split('.')[1]));
        const currentTime = Date.now() / 1000;
        
        if (tokenPayload.exp > currentTime) {
          // Token hala geçerli - rol kontrolü yap
          if (userData.role !== 'hotel_admin' && userData.role !== 'super_admin') {
            localStorage.removeItem('adminToken');
            localStorage.removeItem('adminUser');
            setIsAuthenticated(false);
            setUser(null);
            setToken(null);
          } else {
            // Token hala geçerli ve kullanıcı hotel_admin - hemen state'i güncelle
            setToken(savedToken);
            setUser(userData);
            setIsAuthenticated(true);
          }
        } else {
          // Token süresi dolmuş - ama logout yapma, modal göster
          // Kullanıcı bilgilerini koru, sadece token'ı null yap
          setToken(null);
          setIsAuthenticated(false);
          setShowTokenExpiredModal(true);
        }
      } catch (error) {
        // Clear invalid data
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        setIsAuthenticated(false);
        setUser(null);
        setToken(null);
      }
    } else {
      // No token found
      setIsAuthenticated(false);
      setUser(null);
      setToken(null);
    }
    
    // Mark initialization as complete
    setIsInitialized(true);
  }, []);

  // Listen for token expired events from API calls
  useEffect(() => {
    const handleTokenExpiredEvent = () => {
      // Event geldiyse token expire olmuş demektir
      // Direkt modal göster, tekrar kontrol etmeye gerek yok
      setToken(null);
      setIsAuthenticated(false);
      setShowTokenExpiredModal(true);
    };

    window.addEventListener('tokenExpired', handleTokenExpiredEvent);
    return () => {
      window.removeEventListener('tokenExpired', handleTokenExpiredEvent);
    };
  }, []);

  // Token validation - sadece token expire olduğunda şifre iste
  // Inactivity timeout kaldırıldı - sadece token expire kontrolü yapılıyor (backend'de 7 gün)

  const login = async (email: string, password: string): Promise<{ success: boolean; message?: string; remainingAttempts?: number; lockTime?: number }> => {
    try {
      
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.STAFF_LOGIN}`, {
        method: 'POST',
        headers: DEFAULT_HEADERS,
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (data.success && data.token) {
        // Sadece hotel_admin ve super_admin rolündeki kullanıcılar admin paneline girebilir
        if (data.staff.role !== 'hotel_admin' && data.staff.role !== 'super_admin') {
          return {
            success: false,
            message: 'Bu staff hesabı admin paneline erişim yetkisine sahip değil. Sadece hotel_admin ve super_admin rolündeki kullanıcılar giriş yapabilir.'
          };
        }

        // Token ve kullanıcı bilgilerini kaydet
        localStorage.setItem('adminToken', data.token);
        localStorage.setItem('adminUser', JSON.stringify(data.staff));
        
        setToken(data.token);
        setUser(data.staff);
        setIsAuthenticated(true);
        
        return { success: true };
      } else {
        return {
          success: false,
          message: data.message || 'Giriş başarısız',
          remainingAttempts: data.remainingAttempts,
          lockTime: data.lockTime
        };
      }
    } catch (error) {
      return {
        success: false,
        message: 'Giriş sırasında bir hata oluştu'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    setIsAuthenticated(false);
    setUser(null);
    setToken(null);
  };

  const refreshProfile = async (): Promise<void> => {
    if (!token) return;

    try {
      // Önce token'ın expire olup olmadığını kontrol et
      try {
        const tokenPayload = JSON.parse(atob(token.split('.')[1]));
        const currentTime = Date.now() / 1000;
        
        // Token zaten expire olmuşsa modal göster, logout yapma
        if (tokenPayload.exp <= currentTime) {
          setToken(null);
          setIsAuthenticated(false);
          setShowTokenExpiredModal(true);
          return;
        }
      } catch (e) {
        // Token parse edilemiyorsa modal göster
        setToken(null);
        setIsAuthenticated(false);
        setShowTokenExpiredModal(true);
        return;
      }

      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.STAFF_PROFILE}`, {
        method: 'GET',
        headers: getAuthHeaders(token),
      });

      if (!response.ok) {
        // 401 Unauthorized durumunda - handleTokenExpired kullan
        if (response.status === 401) {
          const { handleTokenExpired } = await import('../config/api');
          handleTokenExpired(response);
        }
        // Diğer HTTP hatalarında logout yapma
        return;
      }

      const data = await response.json();

      if (data.success && data.staff) {
        // Kullanıcının rolünü kontrol et - hotel_admin veya super_admin değilse logout yap
        if (data.staff.role !== 'hotel_admin' && data.staff.role !== 'super_admin') {
          logout();
          return;
        }
        
        localStorage.setItem('adminUser', JSON.stringify(data.staff));
        setUser(data.staff);
      }
      // API success: false durumunda logout yapma
    } catch (error) {
      // Network hatası durumunda logout yapma - kullanıcı hareketsiz kalınca logout yapma
    }
  };

  const refreshToken = async (password: string): Promise<{ success: boolean; message?: string }> => {
    if (!user) {
      return { success: false, message: 'Kullanıcı bilgisi bulunamadı' };
    }

    try {
      const result = await login(user.email, password);
      if (result.success) {
        // Token yenilendi, profile'ı refresh et ve modal'ı kapat
        setShowTokenExpiredModal(false);
        // Profile'ı refresh et ki yeni token ile API çağrıları yapılsın
        await refreshProfile();
        return { success: true };
      } else {
        return { success: false, message: result.message || 'Şifre hatalı' };
      }
    } catch (error) {
      return { success: false, message: 'Token yenileme sırasında bir hata oluştu' };
    }
  };

  const closeTokenExpiredModal = () => {
    // Modal'ı kapatmak yerine logout yap (kullanıcı şifre vermek istemiyorsa)
    logout();
  };

  const value: AuthContextType = {
    isAuthenticated,
    user,
    token,
    isInitialized,
    showTokenExpiredModal,
    login,
    logout,
    refreshProfile,
    refreshToken,
    closeTokenExpiredModal,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

